/*
 * Route-driven state container for a singular Revision
 */

import { cloneElement, Component, useEffect } from 'react';
import { connect } from 'react-redux';
import _ from 'underscore';
import type { FunctionComponent } from 'react';

import RevisionActions from '../../actions/RevisionActions';
import { isDependencyCacheValid } from '../../helpers/revisions';
import NavigationStore from '../../redux/navigation';
import RevisionStore from '../../stores/RevisionStore';
import NotFound from '../generic/NotFound';
import type { NavigationState } from '../../redux/navigation';
import type { RouterProps } from '../../types';

import { RevisionProvider, useRevision } from './contexts/RevisionContext';
import { useRevisionScanIdQueryParam } from './hooks/useRevisionScanIdQueryParam';
import type { ProjectDashboardProject, ProjectDashboardRevision } from './projectDashboardTypes';

// The paths below only require the revision itself and don't use the included issues, dependencies and scans
// that you get when you make the full request.
const minimalRequestOnly = (props) => {
  const minimalPaths = ['issues/licensing', 'issues/quality', 'issues/security'];
  const path = props.routes.slice(-2).map((r) => r.path).join('/');
  return minimalPaths.includes(path);
};

const pollRevision = _.debounce((props, locator) => {
  if (minimalRequestOnly(props)) {
    RevisionActions.loadMinimal(locator, true, true);
  } else {
    RevisionActions.load(locator, true, true);
  }
}, 7000);

type RevisionContainerProps = RouterProps & {
  routes?: any;
  project: ProjectDashboardProject;
  onProjectChange: () => void;
  children?: any;
};

type RevisionContainerState = {
  revisionData: ProjectDashboardRevision;
};

type DispatchProps = {
  setNavState: (newState: Partial<NavigationState>) => void;
};

type RevisionScanIdContainerProps = {
  /**
   * This is a stop-gap while the selectedRevisionScan lives in the Reflux store.
   */
  onSetSelectedRevision: (revisionScanId: number) => void;
};

/**
 * [TODO] This component is going away with https://fossa.atlassian.net/browse/CORE-6026
 * Instead of converting the monstrosity that is RevisionContainer to a function component,
 * we're just going to have a "container" which manages the stateful stuff we care about.
 */
const RevisionScanIdContainer: FunctionComponent<RevisionScanIdContainerProps> = ({
  children,
  onSetSelectedRevision,
}) => {
  const revision = useRevision();
  const { revisionScanId, setRevisionScanId } = useRevisionScanIdQueryParam();

  //  On the iniital load, if the revisionScanId is not present, we set it to the latestRevisionScanId.
  useEffect(() => {
    if (!revisionScanId && revision.data?.latestRevisionScanId) {
      setRevisionScanId(revision.data.latestRevisionScanId);
    }
  }, [revision.data?.latestRevisionScanId, revisionScanId, setRevisionScanId]);

  // Validate that revisionScanId exists in revisionScans array
  // This will unset the query param if the revisionScanId is not valid,
  // which causes the above hook to be re-run, setting the latestRevisionScanId if no revisionScanId is present.
  useEffect(() => {
    // Only validate if we have a revisionScanId, revision is not loading, and revision data is loaded
    if (revisionScanId && !revision.status.loading && revision.data?.revisionScans) {
      // Check if the revisionScanId exists in the array using .some() for early return
      const isValid = revision.data.revisionScans.some(
        revisionScan => revisionScan.id === revisionScanId,
      );

      // If not valid, unset the query param
      if (!isValid) {
        setRevisionScanId(undefined);
        // The existing useEffect above will then set it to latestRevisionScanId
        // (or leave it undefined if no latestRevisionScanId exists)
      }
    }
  }, [revision.data?.revisionScans, revision.status.loading, revisionScanId, setRevisionScanId]);

  //  Separate effect for syncing the selectedRevisionScanId to the Reflux store.
  useEffect(() => {
    if (revisionScanId) {
      onSetSelectedRevision(revisionScanId);
    }
  }, [revisionScanId, onSetSelectedRevision]);

  // eslint-disable-next-line react/jsx-no-useless-fragment -- For TS
  return <>{children}</>;
};

class RevisionContainer extends Component<RevisionContainerProps & DispatchProps, RevisionContainerState> {
  static defaultProps = {
    project: {},
  };

  constructor(props) {
    super(props);
    this.state = {
      revisionData: {
        data: {},
        status: {
          loading: true,
          error: null,
        },

        // calculated on save because it's expensive
        issues: {},
      },
    };
  }

  componentDidMount() {
    this.unsubscribeRevision = (RevisionStore as any).listen(this.onRepoChange);
    return this.onReload(this.props);
  }

  componentDidUpdate(prevProps: Readonly<RevisionContainerProps>) {
    if (
      this.props.params.revision_id !== prevProps.params.revision_id ||
      // Reload if you move to a tab that requires the full revision and you have not yet loaded the full revision.
      (!minimalRequestOnly(this.props) && !this.state.revisionData.status.fullRevisionLoaded &&
        this.props.location.pathname !== prevProps.location.pathname)
    ) {
      this.onReload(this.props);
    }
  }

  componentWillUnmount() {
    if (this.unsubscribeRevision) {
      this.unsubscribeRevision();
    }
  }

  onReload = async (nextProps?: RevisionContainerProps) => {
    const props = nextProps || this.props;
    if (props.params.revision_id) {
      const loc = `${props.params.project_id}$${props.params.revision_id}`;

      if (minimalRequestOnly(props)) {
        return RevisionActions.loadMinimal(loc);
      }

      return RevisionActions.load(loc);
    }
  };

  onRepoChange = repos => {
    const nextState = repos[`${this.props.params.project_id}$${this.props.params.revision_id}`];
    if (nextState?.data?.locator) {
      this.props.setNavState({
        navProps: {
          revision: nextState.data,
        },
      });

      // Unsteady state, poll revision for updates
      if (
        !nextState.data.resolved ||
        !nextState.data.meta?.[0]?.last_scan ||
        !isDependencyCacheValid(nextState.data)
      ) {
        pollRevision(this.props, nextState.data.locator);
      }
    }
    if (nextState) {
      this.setState({ revisionData: nextState });
    }
  };

  onSetSelectedRevision = (revisionScanId: number) =>
    this.setState(state => {
      const newRevisionData = {
        ...state.revisionData?.data ?? {},
        selectedRevisionScan: state.revisionData?.data?.revisionScans?.find(revScan => revScan.id === revisionScanId),
      };
      return {
        ...state,
        revisionData: {
          ...state.revisionData,
          data: newRevisionData,
        },
      } as RevisionContainerState;
    });

  unsubscribeRevision;

  render() {
    if (this.state.revisionData.status.error) {
      const err = this.state.revisionData.status.error.message || this.state.revisionData.status.error;
      if (typeof err === 'string' && err.toLowerCase().includes('not found')) {
        return <NotFound />;
      }
    }

    return (
      <RevisionProvider value={this.state.revisionData}>
        <RevisionScanIdContainer onSetSelectedRevision={this.onSetSelectedRevision}>
          {this.props.children &&
            cloneElement(this.props.children, {
              project: this.props.project,
              onProjectChange: this.props.onProjectChange,
              revision: this.state.revisionData,
            })}
        </RevisionScanIdContainer>
      </RevisionProvider>
    );
  }
}

const mapDispatchToProps: DispatchProps = {
  setNavState: NavigationStore.actions.setNavState,
};

export default connect(null, mapDispatchToProps)(RevisionContainer);
